import React, { useEffect, useMemo, useState } from 'react'
import { useClient } from 'Data/Api'
import { useDataChange, useDataValue } from 'Simple/Data.js'
import TysiaText from 'DesignSystem/TysiaText/index.js'
import query from './txStatusQuery.graphql.js'

import PatientNotFoundCard from './PatientNotFoundCard/index.js'
import SyncPatientMatchingCard from 'DesignSystem/SyncPatientMatchingCard/index.js'
import View from './view.js'

let MATCH_TYPES = /** @type {const} */ ({
  NO_MATCH: 'NO_MATCH',
  PARTIAL: 'PARTIAL',
  MATCH: 'MATCH',
})

export default function Logic(props) {
  let patients = useDataValue({
    context: 'patients',
    viewPath: props.viewPath,
  })
  let filters = useDataValue({
    context: 'filters',
    viewPath: props.viewPath,
  })

  let client = useClient()

  let [filteredPatients, setFilteredPatients] = useState(patients)

  useEffect(() => {
    async function run() {
      let internalPatientIds = Array.isArray(patients)
        ? patients.filter(p => p.internal).map(p => p.internal.id)
        : []

      if (!patients || !filters) return
      let fpatients =
        filters.type === 'ALL'
          ? patients
          : patients.filter(item => filters.type === item.type)

      if (filters.status.length > 0 && internalPatientIds.length) {
        let response = await client.query(query, {
          patient_ids: internalPatientIds,
          statuses: filters.status,
        })
        if (response.error) return

        let filteredPatientIds = new Set(
          response.data.vaxiom_patients.map(p => p._id)
        )
        fpatients = patients.filter(
          item => item.internal && filteredPatientIds.has(item.internal.id)
        )
      }

      setFilteredPatients(fpatients)
    }
    run()
  }, [filters, patients])

  let changePatients = useDataChange({
    context: 'patients',
    viewPath: props.viewPath,
  })

  let columns = useMemo(
    () => [
      {
        header: 'External',
        accessorKey: 'external',
        enableSorting: false,
        cell: cell => {
          if (cell.row.original.type === 'SECTION') {
            return (
              <TysiaText
                marginLeft={16}
                text={cell.row.original.text}
                viewPath={`${props.viewPath}/TysiaText(${cell.row.original.id})`}
              />
            )
          }

          return (
            <SyncPatientMatchingCard
              configuration={cell.row.original}
              source="external"
              error={cell.row.original.error}
              viewPath={`${props.viewPath}/SyncPatientMatchingCard(${cell.row.original.id})`}
            />
          )
        },
      },
      {
        header: 'Greyfinch',
        accessorKey: 'internal',
        enableSorting: false,
        cell: cell => {
          if (cell.row.original.type === 'SECTION') {
            return null
          }

          return cell.getValue() === null ? (
            <PatientNotFoundCard
              onReplaceMatch={handleReplaceMatch}
              viewPath={`${props.viewPath}/PatientNotFoundCard(${cell.row.original.id})`}
            />
          ) : (
            <SyncPatientMatchingCard
              configuration={cell.row.original}
              source="internal"
              onToggleSelection={() => {
                changePatients(next => {
                  let index = next.findIndex(
                    item => item.id === cell.row.original.id
                  )

                  if (index !== -1) {
                    next[index].selected = !next[index].selected
                  }
                })
              }}
              onReplaceMatch={handleReplaceMatch}
              onRemoveMatch={() => {
                changePatients(next => {
                  let index = next.findIndex(
                    item => item.id === cell.row.original.id
                  )
                  if (index !== -1) {
                    next[index].selected = false
                    next[index].internal = null
                    next[index].type = MATCH_TYPES.NO_MATCH
                  }
                })
              }}
              viewPath={`${props.viewPath}/SyncPatientMatchingCard(${cell.row.original.id})`}
            />
          )

          function handleReplaceMatch(internal) {
            changePatients(next => {
              let index = next.findIndex(
                item => item.id === cell.row.original.id
              )
              if (index !== -1) {
                next[index].internal = internal
                next[index].type = MATCH_TYPES.MATCH
                next[index].selected = true
              }
            })
          }
        },
      },
    ],
    [props.viewPath]
  )

  let data = useMemo(() => {
    if (!Array.isArray(filteredPatients)) return []

    return [
      { type: 'SECTION', text: 'MATCHES', color: 'green' },
      ...filteredPatients.filter(item => item.type === MATCH_TYPES.MATCH),
      { type: 'SECTION', text: 'PARTIAL MATCHES', color: 'blue' },
      ...filteredPatients.filter(item => item.type === MATCH_TYPES.PARTIAL),
      { type: 'SECTION', text: 'NO MATCH', color: 'red' },
      ...filteredPatients.filter(item => item.type === MATCH_TYPES.NO_MATCH),
    ]
  }, [filteredPatients])

  return (
    <View
      value={{
        columns,
        data,
      }}
      viewPath={props.viewPath}
    />
  )
}
